import type { CastRow, PropRow } from "@/components/prompt-editor/mention-sources"
import { acceptJsonHeaders, csrfHeaders, handleJson, jsonHeaders } from "@/lib/json-request"
import {
  canvasActivePromptDoc,
  canvasActivePromptText,
  canvasActiveReferences,
  canvasPersistableReferenceItem,
  type CanvasBoardRow,
  type CanvasDraft,
  type CanvasFeedScope,
  type CanvasGenerationRow,
  type CanvasGenerationsPage,
  type CanvasMediaKind,
  type CanvasReferenceItem,
  type CanvasShotTreeEpisode,
} from "@/types/canvas"
import type { StudioQualityValue } from "@/components/studio-quality-mark"
import type { TimelineDocument } from "@/lib/timeline"
import type { ProjectLocationRow } from "@/components/canvas/canvas-create-shot-wizard"
import { canvasApiBase, resolveCanvasApiScope, type CanvasApiScope } from "@/lib/canvas-scope"

export type { CanvasApiScope }
export { canvasApiBase, resolveCanvasApiScope }

/** Feed / cache owner key: `project:123` or `company:456`. */
export function canvasFeedOwnerKey(scope: CanvasApiScope, companyId?: number): string {
  if (scope.kind === "company") {
    if (companyId == null) throw new Error("companyId required for company canvas feed key")
    return `company:${companyId}`
  }
  return `project:${scope.projectId}`
}

function canvasScopeQuery(canvasId: CanvasFeedScope): URLSearchParams {
  const params = new URLSearchParams()
  if (canvasId === "all") params.set("canvas", "all")
  else params.set("canvas_id", String(canvasId))
  return params
}

export type CanvasUploadedReference = {
  signed_id: string
  /** id блоба — источник для назначения кадром: сырой signed_id сервер не принимает. */
  id?: number
  url: string
  /** Full original same-origin proxy for sketch. */
  download_url?: string | null
  thumb_url?: string | null
  media_type: "image" | "video" | "audio"
  label: string
  created_at?: string | null
  duration_seconds?: number | null
}

export type CanvasLibraryReference = CanvasUploadedReference

/** Загрузки одной доски — папка доски в Библиотеке и стартовая папка пикера Холста. */
export async function fetchCanvasBoardReferenceLibrary(
  scope: CanvasApiScope | number,
  canvasId: number,
  options?: { signal?: AbortSignal },
): Promise<CanvasLibraryReference[]> {
  const s = resolveCanvasApiScope(scope)
  const params = new URLSearchParams({ canvas_id: String(canvasId) })
  const res = await fetch(`${canvasApiBase(s)}/board_reference_library?${params.toString()}`, {
    method: "GET",
    credentials: "same-origin",
    signal: options?.signal,
    headers: acceptJsonHeaders(),
  })
  const data = await handleJson<{ ok: boolean; references: CanvasLibraryReference[] }>(res)
  return data.references
}

/**
 * Отмена собственной загрузки — единственное удаление файла, доступное пользователю: из
 * Библиотеки не удаляется ничто. canvasId обязателен, потому что снимается вложение ровно
 * одной доски: тот же файл бывает загружен и на соседние, а их пользователь не трогал.
 * Сервер отказывает (422), если файл уже уехал в генерацию.
 */
export async function cancelCanvasReferenceUpload(
  scope: CanvasApiScope | number,
  signedId: string,
  canvasId: number,
): Promise<void> {
  const s = resolveCanvasApiScope(scope)
  const params = new URLSearchParams({ signed_id: signedId, canvas_id: String(canvasId) })
  const res = await fetch(`${canvasApiBase(s)}/references?${params.toString()}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: csrfHeaders(),
  })
  await handleJson<{ ok: boolean }>(res)
}

export async function uploadCanvasReference(
  scope: CanvasApiScope | number,
  file: File,
  canvasId?: number,
  modelId?: string | null,
): Promise<CanvasUploadedReference> {
  const s = resolveCanvasApiScope(scope)
  const form = new FormData()
  form.append("file", file)
  // Без canvas_id сервер возьмёт первую доску проекта — ок для sketch вне Холста.
  if (canvasId != null) {
    form.append("canvas_id", String(canvasId))
  }
  if (modelId) {
    form.append("model_id", modelId)
  }
  const res = await fetch(`${canvasApiBase(s)}/references`, {
    method: "POST",
    credentials: "same-origin",
    headers: csrfHeaders(),
    body: form,
  })
  const data = await handleJson<{ ok: boolean; reference: CanvasUploadedReference }>(res)
  return data.reference
}

// Дерево для пикера кадров раньше добывалось Inertia-partial-запросом к странице Холста —
// хрупкий хак, ломавшийся 409-м при рассинхроне версии после деплоя. Теперь у дерева есть
// собственный JSON-endpoint: см. fetchLibraryTree в lib/library-api.ts.

export const CANVAS_INITIAL_PAGE_SIZE = 50
export const CANVAS_PAGE_SIZE = 30

export type CanvasExportProject = {
  id: number
  name: string
  cover_image_url?: string | null
}

export type CanvasSaveContext = {
  shot_tree: CanvasShotTreeEpisode[]
  characters: CastRow[]
  locations: ProjectLocationRow[]
  props?: PropRow[]
  export_projects?: CanvasExportProject[]
}

/** Сосед по доске флоу для полосы сравнения в ленте. */
export type CanvasGenerationPeer = {
  id: number
  number: number
  kind: string
  media_url: string | null
  thumb_url: string | null
  duration_ms: number | null
  aspect_ratio: string | null
  created_at: string
}

/** Куда ведёт «Открыть во флоу»: доска и нода, либо missing, если ноду сняли. */
export type CanvasGenerationFlowTarget = {
  number?: number | null
  node_id?: number
  title?: string | null
  missing?: boolean
}

export type CanvasGenerationShowResponse = {
  ok: boolean
  generation: CanvasGenerationRow
  effective_video_output_fps?: number
  save_context?: CanvasSaveContext
  /** Только доска флоу: готовые генерации того же вида. */
  peers?: CanvasGenerationPeer[]
  flow?: CanvasGenerationFlowTarget | null
}

export async function fetchCanvasGeneration(
  scope: CanvasApiScope | number,
  canvasId: CanvasFeedScope,
  generationId: number,
  options?: { includeSaveContext?: boolean; includePeers?: boolean },
): Promise<CanvasGenerationShowResponse> {
  const s = resolveCanvasApiScope(scope)
  const params = canvasScopeQuery(canvasId)
  if (s.kind === "company" && s.companyId != null && s.companyId > 0) {
    params.set("company_id", String(s.companyId))
  }
  if (options?.includeSaveContext) params.set("include_save_context", "1")
  if (options?.includePeers) params.set("include_peers", "1")

  const res = await fetch(`${canvasApiBase(s)}/generations/${generationId}?${params.toString()}`, {
    method: "GET",
    credentials: "same-origin",
    headers: acceptJsonHeaders(),
  })
  return handleJson<CanvasGenerationShowResponse>(res)
}

export async function fetchCanvasGenerations(
  scope: CanvasApiScope | number,
  canvasId: CanvasFeedScope,
  options?: { before?: number | null; limit?: number; status?: "failed"; number?: number },
): Promise<CanvasGenerationsPage> {
  const s = resolveCanvasApiScope(scope)
  const params = canvasScopeQuery(canvasId)
  if (options?.before != null) params.set("before", String(options.before))
  if (options?.limit != null) params.set("limit", String(options.limit))
  if (options?.status) params.set("status", options.status)
  if (options?.number != null) params.set("number", String(options.number))

  const res = await fetch(`${canvasApiBase(s)}/generations?${params.toString()}`, {
    method: "GET",
    credentials: "same-origin",
    headers: acceptJsonHeaders(),
  })
  return handleJson<CanvasGenerationsPage>(res)
}

export async function createCanvasGeneration(
  scope: CanvasApiScope | number,
  canvasId: number,
  draft: CanvasDraft,
): Promise<CanvasGenerationRow> {
  const s = resolveCanvasApiScope(scope)
  const activeRefs = canvasActiveReferences(draft)
  const references: CanvasReferenceItem[] = activeRefs.map(canvasPersistableReferenceItem)
  const referenceSignedIds = activeRefs.map((ref) => ref.signed_id).filter((sid): sid is string => Boolean(sid))

  const res = await fetch(`${canvasApiBase(s)}/generations?canvas_id=${canvasId}`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({
      media_kind: draft.mediaKind,
      model_id: draft.modelId,
      aspect_ratio: draft.aspectRatio,
      duration_sec: draft.durationSec,
      prompt_text: canvasActivePromptText(draft),
      prompt_doc: canvasActivePromptDoc(draft),
      use_project_styles: draft.useProjectStyles,
      model_params: {
        ...draft.modelParams,
        ...(draft.mediaKind === "video" ? { video_consider_scale: draft.videoConsiderScale ? "true" : "false" } : {}),
      },
      reference_items: references,
      reference_signed_ids: referenceSignedIds,
    }),
  })
  const data = await handleJson<{ ok: boolean; generation: CanvasGenerationRow }>(res)
  return data.generation
}

export async function finalizeCanvasGeneration(
  scope: CanvasApiScope | number,
  canvasId: number,
  generationId: number,
  resolution: string,
): Promise<CanvasGenerationRow> {
  const s = resolveCanvasApiScope(scope)
  const res = await fetch(`${canvasApiBase(s)}/generations/${generationId}/finalize_draft?canvas_id=${canvasId}`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({ resolution }),
  })
  const data = await handleJson<{ ok: boolean; generation: CanvasGenerationRow }>(res)
  return data.generation
}

export async function retryCanvasGeneration(
  scope: CanvasApiScope | number,
  canvasId: number,
  generationId: number,
): Promise<CanvasGenerationRow> {
  const s = resolveCanvasApiScope(scope)
  const res = await fetch(`${canvasApiBase(s)}/generations/${generationId}/retry?canvas_id=${canvasId}`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
  })
  const data = await handleJson<{ ok: boolean; generation: CanvasGenerationRow }>(res)
  return data.generation
}

export async function createCanvasGenerationFromSketch(
  scope: CanvasApiScope | number,
  canvasId: number,
  sourceGenerationId: number,
  file: File,
): Promise<CanvasGenerationRow> {
  const s = resolveCanvasApiScope(scope)
  const form = new FormData()
  form.append("file", file)
  form.append("source_generation_id", String(sourceGenerationId))
  const res = await fetch(`${canvasApiBase(s)}/generations/from_sketch?canvas_id=${canvasId}`, {
    method: "POST",
    credentials: "same-origin",
    headers: csrfHeaders(),
    body: form,
  })
  const data = await handleJson<{ ok: boolean; generation: CanvasGenerationRow }>(res)
  return data.generation
}

export async function deleteCanvasGeneration(
  scope: CanvasApiScope | number,
  canvasId: CanvasFeedScope,
  generationId: number,
): Promise<void> {
  const s = resolveCanvasApiScope(scope)
  const params = canvasScopeQuery(canvasId)
  const res = await fetch(`${canvasApiBase(s)}/generations/${generationId}?${params.toString()}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: csrfHeaders(),
  })
  await handleJson<{ ok: boolean }>(res)
}

export async function updateCanvasGenerationStudioQuality(
  scope: CanvasApiScope | number,
  canvasId: CanvasFeedScope,
  generationId: number,
  studioQuality: StudioQualityValue,
): Promise<CanvasGenerationRow> {
  const s = resolveCanvasApiScope(scope)
  const params = canvasScopeQuery(canvasId)
  const res = await fetch(`${canvasApiBase(s)}/generations/${generationId}/studio_quality?${params.toString()}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({ studio_quality: studioQuality }),
  })
  const data = await handleJson<{ ok: boolean; generation: CanvasGenerationRow }>(res)
  return data.generation
}

export async function updateCanvasGenerationTimeline(
  scope: CanvasApiScope | number,
  canvasId: CanvasFeedScope,
  generationId: number,
  timeline: TimelineDocument | Record<string, never>,
): Promise<CanvasGenerationRow> {
  const s = resolveCanvasApiScope(scope)
  const params = canvasScopeQuery(canvasId)
  const res = await fetch(`${canvasApiBase(s)}/generations/${generationId}/timeline?${params.toString()}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({ timeline }),
  })
  const data = await handleJson<{ ok: boolean; generation: CanvasGenerationRow }>(res)
  return data.generation
}

export async function createCanvasBoard(scope: CanvasApiScope | number): Promise<CanvasBoardRow> {
  const s = resolveCanvasApiScope(scope)
  const res = await fetch(`${canvasApiBase(s)}/canvases`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({}),
  })
  const data = await handleJson<{ ok: boolean; canvas: CanvasBoardRow }>(res)
  return data.canvas
}

export async function renameCanvasBoard(
  scope: CanvasApiScope | number,
  canvasId: number,
  title: string,
): Promise<CanvasBoardRow> {
  const s = resolveCanvasApiScope(scope)
  const res = await fetch(`${canvasApiBase(s)}/canvases/${canvasId}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({ title }),
  })
  const data = await handleJson<{ ok: boolean; canvas: CanvasBoardRow }>(res)
  return data.canvas
}

export type CanvasModelPreferences = {
  image_model_id?: string | null
  video_model_id?: string | null
  composer_state?: {
    media_kind?: CanvasMediaKind
    aspect_ratio?: string | null
    duration_sec?: number | null
    use_project_styles?: boolean
    prompt_text_by_kind?: Partial<Record<CanvasMediaKind, string>>
    model_id_by_kind?: Partial<Record<CanvasMediaKind, string>>
    reference_items_by_kind?: Partial<Record<CanvasMediaKind, CanvasReferenceItem[]>>
    model_params_by_kind?: Partial<Record<CanvasMediaKind, Record<string, string>>>
  }
}

export async function updateCanvasPreferences(
  scope: CanvasApiScope | number,
  canvasId: number,
  prefs: CanvasModelPreferences,
): Promise<CanvasBoardRow> {
  const s = resolveCanvasApiScope(scope)
  const res = await fetch(`${canvasApiBase(s)}/canvases/${canvasId}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(prefs),
  })
  const data = await handleJson<{ ok: boolean; canvas: CanvasBoardRow }>(res)
  return data.canvas
}

export async function deleteCanvasBoard(
  scope: CanvasApiScope | number,
  canvasId: number,
): Promise<{ next_canvas_id: number | null; next_canvas_number: number | null }> {
  const s = resolveCanvasApiScope(scope)
  const res = await fetch(`${canvasApiBase(s)}/canvases/${canvasId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: csrfHeaders(),
  })
  const data = await handleJson<{
    ok: boolean
    next_canvas_id: number | null
    next_canvas_number?: number | null
  }>(res)
  return {
    next_canvas_id: data.next_canvas_id ?? null,
    next_canvas_number: data.next_canvas_number ?? null,
  }
}

export type CanvasCharacterAssetSlot = "portrait" | "reference"
export type CanvasPropAssetSlot = "front_view" | "reference"

export type CanvasSaveTarget =
  | {
      target: "character"
      name?: string
      character_id?: number
      asset_slot?: CanvasCharacterAssetSlot
      task_id?: number | null
    }
  | { target: "location"; name?: string; location_id?: number; facing?: string; task_id?: number | null }
  | {
      target: "prop"
      name?: string
      prop_id?: number
      asset_slot?: CanvasPropAssetSlot
      task_id?: number | null
    }
  | { target: "shot_storyboard"; shot_id: number }
  | { target: "shot_shooting_board"; shot_id: number }
  | { target: "shot_video"; shot_id: number }
  | {
      target: "shot_video_new"
      scene_id: number
      insert_after_position: number
      intent_summary?: string
      task_id?: number | null
    }

export async function saveCanvasGenerationToProject(
  scope: CanvasApiScope | number,
  canvasId: number,
  generationId: number,
  payload: CanvasSaveTarget & { project_id?: number },
): Promise<void> {
  const s = resolveCanvasApiScope(scope)
  const res = await fetch(`${canvasApiBase(s)}/generations/${generationId}/save_to_project?canvas_id=${canvasId}`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  })
  await handleJson<{ ok: boolean }>(res)
}

/** Catalogs of a destination project for company-canvas «save to project» dialog. */
export async function fetchCompanyCanvasExportContext(projectId: number): Promise<{
  shot_tree: CanvasShotTreeEpisode[]
  characters: CastRow[]
  locations: ProjectLocationRow[]
  props: PropRow[]
}> {
  const params = new URLSearchParams({ project_id: String(projectId) })
  const res = await fetch(`${canvasApiBase({ kind: "company" })}/export_context?${params.toString()}`, {
    method: "GET",
    credentials: "same-origin",
    headers: acceptJsonHeaders(),
  })
  const data = await handleJson<{
    ok: boolean
    shot_tree: CanvasShotTreeEpisode[]
    characters: CastRow[]
    locations: ProjectLocationRow[]
    props: PropRow[]
  }>(res)
  return {
    shot_tree: data.shot_tree ?? [],
    characters: data.characters ?? [],
    locations: data.locations ?? [],
    props: data.props ?? [],
  }
}

export type CreateCanvasShotBody = {
  scene_id: number
  insert_after_position: number
  intent_summary?: string
  duration_target_sec?: number
  location_variant_id?: number | null
  character_ids?: number[]
  storyboard_image_prompt?: string
  video_kling_prompt?: string
  task_id?: number | null
}

/** Shots always live on a project — not company-scoped. */
export async function createCanvasShot(projectId: number, body: CreateCanvasShotBody): Promise<number> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/create`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  })
  const data = await handleJson<{ ok: boolean; shot_id: number }>(res)
  return data.shot_id
}
